import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, ShieldAlert, Github, FileWarning, X, FileDown, ScanSearch } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { Card, Stat, Spinner } from "@/components/Kit";

const SIG = { Critical: "#A5300F", Notable: "#E39629", Informational: "#7F5F52" };
const DISP = {
  ours: { label: "Ours", c: "#2E7D32" }, monitoring: { label: "Monitoring", c: "#7F5F52" },
  takedown_filed: { label: "Takedown Filed", c: "#E39629" }, blocked: { label: "Blocked", c: "#1F6FEB" },
  benign: { label: "Benign", c: "#8A8175" },
};
const CAT = {
  lookalike_registered: { label: "Lookalike", icon: Globe },
  ct_new_subdomain: { label: "CT Subdomain", icon: ShieldAlert },
  ct_new_issuer: { label: "CT Issuer", icon: ShieldAlert },
  github_code_leak: { label: "GitHub Leak", icon: FileWarning },
  github_repo_mention: { label: "GitHub Repo", icon: Github },
};
const CAT_KEY = {
  lookalike_registered: "lookalike", ct_new_subdomain: "ct_subdomain",
  ct_new_issuer: "ct_issuer", github_code_leak: "github", github_repo_mention: "github",
};
const CAT_TILES = [["lookalike", "Lookalike Domains", Globe], ["ct_subdomain", "CT New Subdomains", ShieldAlert],
  ["ct_issuer", "CT New Issuers", ShieldAlert], ["github", "GitHub Leaks", FileWarning]];

export default function ImpersonationBoard() {
  const { orgId } = useOrg();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [scanning, setScanning] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    http.get("/monitoring/impersonation", { params: { org_id: orgId } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [orgId]);
  useEffect(() => { setData(null); setFilter(""); }, [orgId]);
  useEffect(() => { if (orgId) load(); }, [load, orgId]);

  const discover = useCallback(async () => {
    setScanning(true);
    try {
      await http.post("/monitoring/github/discover", null, { params: { org_id: orgId } });
      toast.success("Scanning GitHub for past leaks — runs in the background, refreshing shortly…");
      setTimeout(() => { load(); setScanning(false); }, 45000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the scan");
      setScanning(false);
    }
  }, [orgId, load]);

  const dispEntries = useMemo(
    () => Object.entries(data?.by_disposition || {}).filter(([, v]) => v > 0),
    [data]);
  const recent = useMemo(
    () => (data?.recent || []).filter((a) => !filter || CAT_KEY[a.change_type] === filter),
    [data, filter]);

  if (loading || !data) return <div className="p-6"><Spinner /></div>;
  const c = data.categories;

  return (
    <div className="p-6 max-w-[1500px] mx-auto fade-up" data-testid="impersonation-board">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#A5300F]">Impersonation & Brand Abuse</h1>
          <p className="text-sm text-[#8A8175] mt-1 mb-5">Unified view of lookalike domains, Certificate Transparency exposure and public GitHub leaks.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={discover} disabled={scanning} data-testid="find-leaks-btn"
            className="flex items-center gap-2 border border-[#E7C9B5] text-[#A5300F] hover:bg-[#FFF5EB] disabled:opacity-50 text-sm font-semibold rounded-full px-4 py-2 transition-colors">
            <ScanSearch className={`w-4 h-4 ${scanning ? "animate-spin" : ""}`} /> {scanning ? "Scanning…" : "Find past leaks"}
          </button>
          <button onClick={() => navigate("/monitoring/exposure-report")} data-testid="export-report-btn"
            className="flex items-center gap-2 bg-[#A5300F] hover:bg-[#8A2708] text-white text-sm font-semibold rounded-full px-4 py-2 transition-colors">
            <FileDown className="w-4 h-4" /> Export Report
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <Stat label="Total Signals" value={data.total} data-testid="stat-total" />
        <Stat label="Open" value={data.open} color="#A5300F" sub={`${data.total - data.open} closed`} />
        <Stat label="Critical" value={data.by_significance.Critical} color="#A5300F" />
        <Stat label="Notable" value={data.by_significance.Notable} color="#E39629" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {CAT_TILES.map(([k, lbl, Icon]) => {
          const active = filter === k;
          return (
            <button key={k} type="button" onClick={() => setFilter(active ? "" : k)} data-testid={`cat-${k}`}
              className="text-left transition-all duration-150"
              style={active ? { transform: "translateY(-2px)" } : undefined}>
              <Card className={`p-4 flex items-center gap-3 cursor-pointer hover:border-[#E7C9B5] ${active ? "border-[#A5300F] ring-2 ring-[#A5300F]/20" : ""}`}>
                <div className="w-10 h-10 rounded-lg bg-[#FFF5EB] flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-[#A5300F]" />
                </div>
                <div>
                  <div className="stat-numeral text-2xl leading-none">{c[k] ?? 0}</div>
                  <div className="text-[11px] uppercase tracking-wider text-[#8A8175] mt-1">{lbl}</div>
                </div>
              </Card>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="disposition-summary">
        {dispEntries.map(([k, v]) => (
          <span key={k} className="text-[11px] px-2.5 py-1 rounded-full font-semibold text-white"
            style={{ backgroundColor: k === "untagged" ? "#B5AC9E" : (DISP[k]?.c || "#8A8175") }}>
            {(DISP[k]?.label || "Untagged")}: {v}
          </span>
        ))}
      </div>

      <Card>
        <div className="px-4 py-3 border-b border-[#ECE6DD] flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wider text-[#8A8175] font-semibold">
            Recent brand-abuse signals{filter && ` · ${CAT_TILES.find(([k]) => k === filter)?.[1]}`}
          </span>
          {filter && (
            <button onClick={() => setFilter("")} data-testid="clear-filter"
              className="flex items-center gap-1 text-[11px] text-[#A5300F] hover:underline">
              <X className="w-3 h-3" /> Clear filter
            </button>
          )}
        </div>
        <div className="divide-y divide-[#F4EFE8]" data-testid="impersonation-list">
          {recent.map((a) => {
            const cat = CAT[a.change_type] || {};
            const Icon = cat.icon || ShieldAlert;
            return (
              <div key={a.id} onClick={() => navigate("/monitoring/inbox")} role="button" tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter") navigate("/monitoring/inbox"); }}
                className="p-4 flex items-start gap-3 cursor-pointer hover:bg-[#FBF7F1] transition-colors" data-testid={`imp-${a.id}`}>
                <Icon className="w-4 h-4 mt-0.5 shrink-0 text-[#8A8175]" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: SIG[a.significance] }}>{a.significance}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FFF5EB] text-[#8A8175] font-semibold">{cat.label || a.change_type}</span>
                    {(a.current_value?.via === "secret_scan" || a.current_value?.sensitive) && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#A5300F] text-white font-bold uppercase tracking-wide" data-testid={`secret-badge-${a.id}`}>Secret Leak</span>
                    )}
                    <span className="font-semibold text-sm truncate">{a.subject_ref}</span>
                    {a.disposition && <span className="text-[10px] px-1.5 py-0.5 rounded text-white font-bold" style={{ backgroundColor: DISP[a.disposition]?.c }}>{DISP[a.disposition]?.label}</span>}
                  </div>
                  <div className="text-sm text-[#5C554C] mt-0.5">{a.rationale}</div>
                  {a.current_value?.url && (
                    <a href={a.current_value.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                      data-testid={`imp-source-${a.id}`}
                      className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#A5300F] underline break-all">
                      <Github className="w-3 h-3" />{a.current_value.repo ? `${a.current_value.repo}${a.current_value.path ? "/" + a.current_value.path : ""}` : a.current_value.url}
                    </a>
                  )}
                  {a.current_value?.snippet && (
                    <pre className="mt-1 text-[10px] bg-[#2B2622] text-[#E8DFD3] rounded p-1.5 overflow-x-auto whitespace-pre-wrap break-all font-mono max-w-full" data-testid={`imp-snippet-${a.id}`}>{a.current_value.snippet}</pre>
                  )}
                </div>
                <span className="text-[11px] text-[#B5AC9E] shrink-0">{new Date(a.detected_at).toLocaleDateString("en-GB")}</span>
              </div>
            );
          })}
          {recent.length === 0 && <div className="p-10 text-center text-[#B5AC9E]">{filter ? "No signals in this category." : "No impersonation signals yet. Watchers will populate this board as they run."}</div>}
        </div>
      </Card>
    </div>
  );
}
